// Power-BI-style theme tokens. Used by ECharts wrappers and CSS variables.

export const palette = [
  "#118DFF", // PBI blue
  "#12239E", // PBI deep blue
  "#E66C37", // burnt orange
  "#6B007B", // purple
  "#E044A7", // pink
  "#744EC2", // violet
  "#D9B300", // gold
  "#D64550", // red
] as const;

export type PaletteIndex = number;

export const theme = {
  palette,
  textColor: "#252423",
  textMuted: "#605E5C",
  axisLine: "#D2D0CE",
  gridLine: "#F3F2F1",
  surface: "#FFFFFF",
  surfaceMuted: "#FAF9F8",
  background: "#F3F2F1",
  border: "#EDEBE9",
  shadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
  radius: 4,
  padding: 12,
  fontFamily:
    '"Segoe UI", "Segoe UI Web (West European)", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize: 12,
} as const;

/** Returns a color from the palette, wrapping around for large series. */
export function colorAt(i: number): string {
  return palette[((i % palette.length) + palette.length) % palette.length];
}

/** Standard ECharts text style used across renderers. */
export const echartsTextStyle = {
  fontFamily: theme.fontFamily,
  fontSize: theme.fontSize,
  color: theme.textColor,
};

/* AGENT-D: Semantic design tokens for the cross-cutting design system.
   Consumed by agents T, C, L, R via CSS variables in index.css and
   directly via TS import where helpful. Names are stable — do not rename. */
export const tokens = {
  color: {
    canvas:           "#ffffff",        // visual / page card surface
    outspace:         "#f3f2f1",        // body bg behind the canvas
    fg:               "#252423",        // primary text
    fgMuted:          "#605e5c",        // secondary text
    border:           "#e0dedd",        // visible divider
    borderSubtle:     "#edebe9",        // hairline
    tableHeaderBg:    "#f8f7f5",
    tableRowAlt:      "#f9f8f7",
    tableRowHover:    "#f3f2f1",
    accent:           "#118dff",        // PBI blue (= existing theme.primary)
    accentSoft:       "#e7f1ff",
    scrollbarThumb:   "#c8c6c4",
    scrollbarThumbHover: "#a19f9d",
    scrollbarTrack:   "#f3f2f1",
  },
  font: {
    family: "'Segoe UI', 'Roboto', system-ui, -apple-system, sans-serif",
    size: {
      body: "13px",          // up from 12px
      small: "12px",
      caption: "12px",
      tableHeader: "12px",
      tableCell: "13px",
      kpiValue: "32px",
      kpiValueWithCaption: "24px",
      visualTitle: "13px",
      pageTitle: "24px",
      pill: "11px",          // up from 8px
    },
    weight: {
      regular: 400,
      semibold: 600,
      bold: 700,
    },
  },
  space: { cardPadding: "12px", cardPaddingTight: "8px" },
  radius: { card: "6px", chip: "12px" },
  shadow: {
    card:      "0 1px 2px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
    cardHover: "0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.06)",
    canvas:    "0 2px 8px rgba(0,0,0,0.08)",
    drawer:    "-12px 0 24px rgba(0,0,0,0.12)",
  },
  z: { drawer: 1000, popover: 1100, modal: 1200 },
} as const;

/* AGENT-A4: Dashboard rebuild design tokens.
   A NEW token namespace sibling to `tokens` above. The existing `tokens`
   export is consumed by the older UI screens and must remain untouched.
   `dashboardTokens` is consumed exclusively by code under
   `webapp/frontend/src/dashboard/` and the agents (A5..A8) building the
   rebuilt dashboard. Names are stable — do not rename. */
export const dashboardTokens = {
  color: {
    // PBI palette alignment (A4 refresh, 2026-05-19).
    appBg:       "#f5f5f5",   // overall page background (PBI page bg)
    cardBg:      "#ffffff",   // card / visual surface
    stripBg:     "#fafafa",   // header / strip surface
    hairline:    "#e0e0e0",   // 1px divider
    fg:          "#1a1a1a",   // primary text
    fgMuted:     "#666666",   // secondary text
    fgSubtle:    "#999999",   // tertiary text
    accent:      "#1a73e8",   // PBI primary blue (active toggles, primary bars)
    accentSoft:  "#e8f0fe",   // accent tint
    success:     "#2e7d32",   // green
    warning:     "#ff8c42",   // PBI orange
    danger:      "#d64550",   // PBI red
    // PBI-specific accents (additive — for combo-chart overlays).
    accentOrange: "#e66c37",
    accentPink:   "#e044a7",
    blueLight:    "#42a5f5",  // Qty Sold
    blueDark:     "#1565c0",  // Qty Quoted
    // Data-bar background for the metrics table (Agent B6).
    dataBar:      "rgba(26, 115, 232, 0.18)",
    // Heatmap stops (additive).
    heatmapLow:   "#fde2e2",
    heatmapMid:   "#fff4c2",
    heatmapHigh:  "#d6efd6",
    chartSeries: [
      "#1a73e8",
      "#12239e",
      "#ff8c42",
      "#6b007b",
      "#e91e8c",
      "#744ec2",
      "#d9b300",
      "#d64550",
    ],
  },
  font: {
    family: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    size: {
      body:        "14px",
      small:       "12px",
      h1:          "24px",
      h2:          "18px",
      h3:          "15px",
      kpi:         "28px",
      tableHeader: "12px",
      tableCell:   "13px",
    },
    weight: {
      regular:  400,
      medium:   500,
      semibold: 600,
      bold:     700,
    },
  },
  space: {
    cardPad:    "16px",   // inside cards
    cardGap:    "20px",   // between cards
    stackGap:   "12px",   // vertical stack default
    formGap:    "8px",    // tight form rows
    sectionGap: "24px",   // between page sections
    inlineGap:  "8px",    // inline elements (icon+text)
  },
  radius: {
    // PBI spec: card radius 8px.
    card:    "8px",
    control: "8px",
    button:  "6px",
    pill:    "999px",
  },
  shadow: {
    card:    "0 1px 3px rgba(15,23,42,0.04)",
    hover:   "0 4px 12px rgba(15,23,42,0.06)",
    drawer:  "-12px 0 32px rgba(15,23,42,0.10)",
    popover: "0 8px 24px rgba(15,23,42,0.08)",
  },
  motion: {
    fast:   "120ms",
    base:   "180ms",
    slow:   "240ms",
    easing: "cubic-bezier(0.2, 0.0, 0.0, 1.0)",
  },
  z: {
    sticky:   100,
    dropdown: 1000,
    drawer:   1100,
    modal:    1200,
    toast:    1300,
  },
} as const;
